<template>
  <div class="health-analysis-container">
    <h2>健康分析</h2>
    
    <!-- 数据概览区域 -->
    <div class="overview-section">
      <div class="overview-card" v-if="healthData">
        <div class="overview-item">
          <span class="label">BMI指数</span>
          <span class="value status-{{ healthData.bmiStatus || getBmiStatus(healthData.bmi) }}">{{ healthData.bmi.toFixed(1) }}</span>
        </div>
        <div class="overview-item">
          <span class="label">BMI状态</span>
          <span class="value status-{{ healthData.bmiStatus || getBmiStatus(healthData.bmi) }}">{{ healthData.bmiStatus || getBmiStatus(healthData.bmi) }}</span>
        </div>
        <div class="overview-item">
          <span class="label">平均体测成绩</span>
          <span class="value">{{ healthData.averageScore.toFixed(1) }}</span>
        </div>
        <div class="overview-item">
          <span class="label">成绩等级</span>
          <span class="value">{{ healthData.scoreLevel || getScoreLevel(healthData.averageScore) }}</span>
        </div>
      </div>
    </div>
    
    <!-- 数据可视化区域 - 第一行 -->
    <div class="visualization-area">
      <!-- 体测成绩分布 -->
      <div class="chart-container">
        <div class="chart-header">
          <h3>体测成绩分布</h3>
          <el-select v-model="selectedActivityId" placeholder="选择体测活动" @change="handleActivityChange" class="activity-select">
            <el-option value="" label="全部活动"></el-option>
            <el-option v-for="activity in activities" :key="activity.id" :label="activity.name" :value="activity.id"></el-option>
          </el-select>
        </div>
        <div class="chart" v-loading="loading" element-loading-text="加载中...">
          <score-distribution-chart ref="scoreDistributionRef" :data="scoreDistributionData"></score-distribution-chart>
        </div>
      </div>
      
      <!-- 项目能力雷达图 -->
      <div class="chart-container">
        <div class="chart-header">
          <h3>项目能力雷达图</h3>
        </div>
        <div class="chart" v-loading="loading" element-loading-text="加载中...">
          <project-radar-chart ref="projectRadarRef" :data="projectRadarData"></project-radar-chart>
        </div>
      </div>
    </div>
    
    <!-- 数据可视化区域 - 第二行 -->
    <div class="visualization-area">
      <!-- 平均成绩趋势图 -->
      <div class="chart-container">
        <h3>平均成绩趋势图</h3>
        <div class="chart" v-loading="loading" element-loading-text="加载中...">
          <average-score-trend-chart ref="averageScoreTrendRef" :data="trendData"></average-score-trend-chart>
        </div>
      </div>
      
      <!-- 健康数据雷达图 -->
      <div class="chart-container">
        <h3>健康数据雷达图</h3>
        <div class="chart" v-loading="loading" element-loading-text="加载中...">
          <project-radar-chart ref="healthDataRadarRef" :data="healthDataRadar"></project-radar-chart>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick } from 'vue';
import { ElMessage } from 'element-plus';
import request from '@/utils/request';
import ScoreDistributionChart from '../../components/charts/ScoreDistributionChart.vue';
import AverageScoreTrendChart from '../../components/charts/AverageScoreTrendChart.vue';
import ProjectRadarChart from '../../components/charts/ProjectRadarChart.vue';

// 状态变量
const loading = ref(true);

// 图表组件引用
const scoreDistributionRef = ref(null);
const projectRadarRef = ref(null);
const averageScoreTrendRef = ref(null);
const healthDataRadarRef = ref(null);

// 健康数据
const healthData = reactive({
  bmi: 0,
  bmiStatus: '',
  averageScore: 0,
  scoreLevel: ''
});

// 图表数据
const scoreDistributionData = ref([]);
const trendData = ref([]);
const projectRadarData = ref({});
const healthDataRadar = ref({});

// 活动相关
const activities = ref([]);
const selectedActivityId = ref('');

// 删除健康建议相关代码

// BMI状态判断
const getBmiStatus = (bmi) => {
  if (bmi < 18.5) return '偏瘦';
  if (bmi < 24) return '正常';
  if (bmi < 28) return '超重';
  return '肥胖';
};

// 成绩等级判断
const getScoreLevel = (score) => {
  if (score >= 90) return '优秀';
  if (score >= 80) return '良好';
  if (score >= 60) return '及格';
  return '不及格';
};

// 页面加载后重新绘制所有图表
const redrawAllCharts = () => {
  nextTick(() => {
    // 调用所有图表组件的triggerRedraw方法
    if (scoreDistributionRef.value) {
      scoreDistributionRef.value.triggerRedraw();
    }
    if (projectRadarRef.value) {
      projectRadarRef.value.triggerRedraw();
    }
    if (averageScoreTrendRef.value) {
      averageScoreTrendRef.value.triggerRedraw();
    }
    if (healthDataRadarRef.value) {
      healthDataRadarRef.value.triggerRedraw();
    }
    
    // 触发窗口大小调整事件确保图表正确渲染
    window.dispatchEvent(new Event('resize'));
  });
};



// 初始化健康数据雷达图
const initHealthDataRadar = () => {
  // 定义健康数据雷达图的指标和模拟数据
  // 根据BMI计算身体质量得分（将BMI转换为0-100的分数）
  const bmiScore = getBmiScore(healthData.bmi);
  
  // 使用动态判断，避免硬编码活动ID
  const mockHealthRadarData = {
    categories: ['身体质量', '力量', '耐力', '柔韧性', '速度'],
    series: [
      {
        name: '个人能力',
        data: selectedActivityId.value === '' 
          ? [bmiScore, 75, 70, 85, 85]  // 默认数据
          : [bmiScore, 75, 70, 85, 85]  // 所有活动通用数据
      }
    ]
  };
  
  healthDataRadar.value = mockHealthRadarData;
};

// 将BMI指数转换为0-100的分数（BMI在18.5-24之间为最佳）
const getBmiScore = (bmi) => {
  if (bmi >= 18.5 && bmi < 24) {
    return 100;  // 正常BMI得满分
  } else if ((bmi >= 17 && bmi < 18.5) || (bmi >= 24 && bmi < 26)) {
    return 80;   // 轻度异常
  } else if ((bmi >= 16 && bmi < 17) || (bmi >= 26 && bmi < 28)) {
    return 60;   // 中度异常
  } else {
    return 40;   // 重度异常
  }
};

// 获取体测活动列表
const fetchActivities = async () => {
  try {
    const response = await request.get('/student/activities');
    // 确保从后端获取所有活动数据，不使用mock数据
    if (response.data && response.data.activities && response.data.activities.length > 0) {
      activities.value = response.data.activities;
      console.log('从后端获取的活动数据:', activities.value);
    } else if (response.data && response.data.length > 0) {
      // 兼容后端直接返回数组的情况
      activities.value = response.data;
      console.log('从后端获取的活动数据(数组格式):', activities.value);
    } else {
      console.warn('后端未返回活动数据');
      activities.value = [];
    }
  } catch (error) {
    console.error('获取活动列表失败:', error);
    // 发生错误时清空活动列表，避免显示错误的mock数据
    activities.value = [];
    ElMessage.error('获取活动列表失败，请刷新页面重试');
  }
};

// 获取健康分析数据
const fetchHealthAnalysis = async (activityId = '') => {
  try {
    loading.value = true;
    // 使用封装的request.js确保token被正确添加
    // 调用正确的后端API接口，支持按活动筛选
    const response = await request.get('/student/health-analysis', activityId ? { activityId } : {});
    const data = response.data;

    // 更新健康数据
      if (data) {
        // 检查数据结构并相应地更新
        if (data.healthData) {
          // 更新所有需要的字段，包括后端返回的BMI状态和成绩等级
          healthData.bmi = data.healthData.bmi || 0;
          healthData.bmiStatus = data.healthData.bmiStatus || '';
          healthData.averageScore = data.healthData.averageScore || 0;
          healthData.scoreLevel = data.healthData.scoreLevel || '';
        } else {
          // 直接使用返回的数据
          healthData.bmi = data.bmi || 0;
          healthData.bmiStatus = data.bmiStatus || '';
          healthData.averageScore = data.averageScore || 0;
          healthData.scoreLevel = data.scoreLevel || '';
        }

        // 更新图表数据
        console.log('后端返回的scoreDistribution数据:', data.scoreDistribution);
        if (data.scoreDistribution && data.scoreDistribution.length > 0) {
          // 确保数据格式正确
          const formattedData = data.scoreDistribution.map(item => ({
            name: item.name || '',
            value: item.value || 0
          }));
          console.log('格式化后的scoreDistribution数据:', formattedData);
          scoreDistributionData.value = formattedData;
        } else {
          console.log('没有scoreDistribution数据');
          // 没有数据时返回空数组，避免显示模拟数据
          scoreDistributionData.value = [];
        }
      
      // 设置趋势图数据
      if (data.trendData && data.trendData.length > 0) {
        trendData.value = data.trendData;
      } else {
        // 没有数据时返回空数组，避免显示模拟数据
        trendData.value = [];
      }
      
      // 设置项目能力雷达图数据
      // 从后端获取的真实数据
      if (data.radarData && data.radarData.categories && data.radarData.values) {
        // 创建与categories长度相同的数组，所有值都为80
          const standardData = new Array(data.radarData.categories.length).fill(80);
        projectRadarData.value = {
          categories: data.radarData.categories,
          series: [
            {
              name: '个人成绩',
              data: data.radarData.values
            },
            {
              name: '标准要求',
              data: standardData // 所有项目标准要求固定为90分
            }
          ]
        };
      } else {
        // 如果后端没有提供数据，使用mock数据作为备选
        // 根据选择的活动返回不同的模拟数据
        const mockRadarData = activityId === '502' 
          ? { categories: ['50米跑', '立定跳远', '引体向上', '肺活量'],
              series: [
                { name: '个人成绩', data: [60, 55, 40, 58] },
                { name: '标准要求', data: [80, 80, 80, 80] } // 标准要求固定为80分
              ]
            }
          : { categories: ['50米跑', '立定跳远', '引体向上', '肺活量'],
              series: [
                { name: '个人成绩', data: [85, 88, 60, 82] },
                { name: '标准要求', data: [80, 80, 80, 80] } // 标准要求固定为80分
              ]
            };
        projectRadarData.value = mockRadarData;
      }
      
      // 初始化健康数据雷达图
      initHealthDataRadar();
    }
  } catch (error) {
    console.error('获取健康分析数据失败:', error);
    ElMessage.error('获取健康分析数据失败');
  } finally {
    loading.value = false;
    console.log('最终设置的scoreDistributionData:', scoreDistributionData.value);
    // 数据加载完成后重新绘制所有图表
    if (!loading.value) {
      redrawAllCharts();
    }
  }
};

// 处理活动选择变化
const handleActivityChange = () => {
  fetchHealthAnalysis(selectedActivityId.value);
};

onMounted(async () => {
  await fetchActivities();
  fetchHealthAnalysis();
});
</script>

<style scoped>
.health-analysis-container {
  padding: 20px;
  background-color: #f5f7fa;
}

.health-analysis-container h2 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 24px;
  font-weight: 600;
}

/* 数据概览区域样式 */
.overview-section {
  margin-bottom: 30px;
}

.overview-card {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.overview-item {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.overview-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

.overview-item .label {
  display: block;
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 8px;
}

.overview-item .value {
  display: block;
  color: #1a202c;
  font-size: 24px;
  font-weight: 600;
}

/* BMI状态颜色 */
.status-偏瘦 {
  color: #3b82f6;
}

.status-正常 {
  color: #10b981;
}

.status-超重 {
  color: #f59e0b;
}

.status-肥胖 {
  color: #ef4444;
}

/* 数据可视化区域样式 */
.visualization-area {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.chart-container {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
}

.chart-container:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.chart-container h3 {
  margin: 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.activity-select {
  width: 200px;
}

.chart {
  width: 100%;
  height: 400px;
}



/* 响应式设计 */
@media (max-width: 1200px) {
  .overview-card {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .health-analysis-container {
    padding: 10px;
  }
  
  .overview-card {
    grid-template-columns: 1fr;
  }
  
  .visualization-area {
    grid-template-columns: 1fr;
  }
  
  .chart {
    height: 300px;
  }
  

}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.overview-item, .chart-container {
  animation: fadeIn 0.5s ease-out;
}
</style>